<section class="max-w-6xl mx-auto p-4 md:p-6">
  <h1 class="text-3xl font-bold mb-8 text-gray">{{ 'Checkout' | translate }}</h1>
  <div class="grid grid-cols-1 lg:grid-cols-3 gap-8">
    <div class="lg:col-span-2 space-y-4">
      <div class="border border-gray-200 rounded-lg p-5">
        <h2 class="text-lg font-semibold text-[#161616] mb-4">
          {{ 'Shipping Address' | translate }}
        </h2>

        @if (defaultAddress(); as address) {
          <div class="flex items-start justify-between">
            <!-- Left Content: Name, Badge, Address Details -->
            <div class="flex flex-col gap-1 text-[#384250]">
              <div class="flex items-center gap-3 mb-1">
                <span class="font-normal text-base text-[#161616]">
                  {{ address.fullName }}
                </span>
                @if (address.isDefault) {
                  <span
                    class="bg-[#EA738C] text-xs px-2 py-0.5 rounded text-white font-normal tracking-wide"
                  >
                    {{ 'Default' | translate }}
                  </span>
                }
              </div>

              <p class="text-sm text-[#475467] leading-relaxed">
                {{ address.streetAddress }}
              </p>
              <p class="text-sm text-[#475467]">
                {{ 'phone number' | translate }} {{ address.phone }}
              </p>
              @if (!address.isDeliveryReady) {
                <p class="text-sm font-medium text-red-700" role="alert">
                  {{ 'addressReadiness.notReady' | translate }}
                </p>
              }
            </div>

            <!-- Right Content: Edit Button -->
            <button
              type="button"
              (click)="editAddress()"
              class="flex items-center gap-1 px-2 py-1 bg-[#F3F4F6] rounded-sm text-sm font-medium hover:bg-gray-200 transition whitespace-nowrap"
            >
              <sham-i-edit-board class="*:size-4" />
              {{ 'Edit Address' | translate }}
            </button>
          </div>
        } @else {
          <div class="flex items-center justify-between">
            <p class="text-sm text-gray-500 py-2">
              {{
                'No shipping address added yet. Please add an address to continue.'
                  | translate
              }}
            </p>
            <button
              (click)="editAddress()"
              class="flex items-center gap-2 px-4 py-1.5 rounded-md text-xs md:text-sm font-medium bg-[#2596BE] text-white hover:bg-[#1a7a9c] transition shadow-sm"
            >
              <sham-i-plus-03 /> {{ 'Add Address' | translate }}
            </button>
          </div>
        }
      </div>
      <div class="lg:col-span-2 space-y-6">
        <div class="hidden lg:grid grid-cols-12 items-center p-4 border rounded-lg">
          <div class="col-span-4 flex items-center gap-3">
            <span>{{ 'Items' | translate }}</span>
          </div>
          <div class="col-span-2 text-center">{{ 'Unit Price' | translate }}</div>
          <div class="col-span-2 text-center">{{ 'Quantity' | translate }}</div>
          <div class="col-span-2 text-center">{{ 'Total' | translate }}</div>
          <div class="col-span-2 text-center">{{ 'Action' | translate }}</div>
        </div>

        @for (store of groupedItems(); track store.sellerId; let sIdx = $index) {
          <div class="border rounded-lg overflow-hidden p-4">
            <div
              class="flex items-center justify-between pb-2 border-b border-gray-100"
            >
              <div class="flex items-center gap-3">
                <div class="flex items-center gap-2">
                  <img
                    [src]="mediaUrl(store.sellerLogoUrl)"
                    alt="logo"
                    class="w-7 h-7 rounded-full"
                  />
                  <span class="text-sm text-[#114455]">{{ store.sellerName }}</span>
                </div>
              </div>
              <a
                [href]="'seller/' + store.sellerId"
                class="text-[#114455] text-xs font-medium underline"
                >{{ 'view_store' | translate }}</a
              >
            </div>

            <div class="flex flex-col">
              @for (item of store.items; track item.checkoutSessionItemId) {
                <!-- DESKTOP -->
                <div
                  class="hidden lg:grid lg:grid-cols-12 items-center px-0 p-4 border-b border-gray-50 gap-4 last:border-none"
                >
                  <!-- PRODUCT -->
                  <div class="col-span-4 flex items-center gap-4">
                    <img
                      [src]="mediaUrl(item.imageUrl)"
                      class="w-20 h-20 object-contain shrink-0"
                    />
                    <div class="flex flex-col gap-1">
                      <h4 class="text-base">{{ item.productName }}</h4>
                      <p class="text-sm text-[#424654] font-normal">
                        {{ getOptionsText(item.options) }}
                      </p>
                    </div>
                  </div>

                  <!-- PRICE -->
                  <div class="col-span-2 text-center">
                    <span class="font-medium text-base">
                      {{ item.unitPrice }} {{ item.unitPriceCurrency }}</span
                    >
                  </div>

                  <!-- QTY -->
                  <div class="col-span-2 flex justify-center">
                    <app-quantity-selector
                      variant="desktop"
                      [qty]="item.quantity"
                      [itemId]="item.shoppingCartItemId"
                      [isCheckout]="true"
                    />
                  </div>

                  <!-- TOTAL -->
                  <div class="col-span-2 text-center">
                    <span class="font-medium text-[#2187AB] text-base">
                      {{ item.totalPrice.toFixed(2) }} {{ item.unitPriceCurrency }}
                    </span>
                  </div>

                  <div class="col-span-2 flex justify-center gap-2">
                    <app-cart-item-actions
                      [mobile]="false"
                      [favorite]="isFavorite(item.productId, item.variantId)"
                      [isCheckout]="true"
                    />
                  </div>
                </div>

                <!-- MOBILE -->
                <div class="lg:hidden py-5 border-b border-[#E6E6E6]">
                  <div class="flex gap-1 min-w-0 items-start">
                    <!-- checkbox -->
                    <div class="pt-2 shrink-0"></div>

                    <!-- image -->
                    <img
                      [src]="mediaUrl(item.imageUrl)"
                      class="w-10 h-12 object-contain shrink-0"
                    />
                    <div class="flex-1 min-w-0">
                      <h4
                        class="text-xs text-[#333] leading-5 line-clamp-2 break-words"
                      >
                        {{ item.productName }}
                      </h4>
                    </div>
                    <!-- content -->
                    <div class="flex-1">
                      <!-- top -->
                      <div class="flex items-start justify-between gap-3">
                        <!-- title -->
                        <div class="font-medium text-xs">
                          {{ item.unitPrice }} {{ item.unitPriceCurrency }}
                        </div>

                        <!-- qty -->
                        <app-quantity-selector
                          variant="mobile"
                          [qty]="item.quantity"
                          [itemId]="item.shoppingCartItemId"
                        />
                      </div>

                      <!-- bottom -->
                      <div class="flex items-end justify-between mt-3">
                        <div class="space-y-2">
                          <div class="font-medium text-[#2187AB] text-xs">
                            {{ item.totalPrice.toFixed(2) }}
                            {{ item.unitPriceCurrency }}
                          </div>
                        </div>

                        <!-- actions -->
                        <app-cart-item-actions
                          [mobile]="true"
                          [favorite]="isFavorite(item.productId, item.variantId)"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              }
            </div>

            @if (hasAppliedCoupon(store.sellerId); as appliedCoupon) {
              <div class="pt-3 md:pt-4 border-t border-gray-100">
                <div class="flex items-center justify-between">
                  <div class="flex items-center gap-2">
                    <span class="text-sm font-medium text-gray-900">
                      {{ 'voucherApplied' | translate }} "{{
                        getAppliedCouponCode(store.sellerId)
                      }}"
                    </span>
                    <button
                      (click)="removeCoupon(store.sellerId)"
                      class="text-sm text-red-600 hover:text-red-800 underline"
                    >
                      {{ 'remove' | translate }}
                    </button>
                  </div>
                  @if (getSellerAppliedCoupon(store.sellerId); as couponData) {
                    <div class="text-sm font-bold text-[#2596BE]">
                      @if (couponData.discountType === 'Percentage') {
                        -{{ couponData.discountValue }}%
                      } @else {
                        -{{ couponData.previewDiscountAmount.toFixed(2) }}
                        {{ couponData.previewDiscountCurrency || '' }}
                      }
                    </div>
                  }
                </div>
                <div
                  class="mt-2 flex items-center gap-2 text-[#1E7898] transition hover:underline cursor-pointer"
                  (click)="openVoucherModal(store.sellerId)"
                >
                  <sham-i-leading />
                  <span class="text-sm font-medium">{{
                    'chooseAnotherVoucher' | translate
                  }}</span>
                </div>
              </div>
            } @else {
              @if (getSellerCoupons(store.sellerId).length) {
                <div
                  class="pt-3 md:pt-4 border-t border-gray-100 flex items-center gap-2 text-[#1E7898] transition hover:underline cursor-pointer"
                  (click)="openVoucherModal(store.sellerId)"
                >
                  <sham-i-leading />
                  <span class="text-sm font-medium">{{
                    'addShopVoucherCode' | translate
                  }}</span>
                </div>
              } @else {
                <div
                  class="flex items-center gap-2 border-t border-gray-100 pt-3 text-[#6D6D6D] md:pt-4"
                >
                  <sham-i-nocoupon class="text-[#6D6D6D]" />
                  <span class="text-sm font-medium">No Vouchers Available Yet</span>
                </div>
              }
            }

            <dl class="mt-4 space-y-2 border-t border-gray-100 pt-4 text-base">
              <div class="flex items-center justify-between text-[#4D4D4D]">
                <dt>{{ 'Delivery Fee' | translate }}</dt>
                <dd class="font-medium text-gray-900">
                  {{ store.deliveryFee.toFixed(2) }} {{ store.currency }}
                </dd>
              </div>
              <div
                class="flex items-center justify-between border-t border-gray-100 pt-2"
              >
                <dt class="font-medium text-[#4D4D4D]">
                  {{ 'Store Total' | translate }}
                </dt>
                <dd class="font-medium text-[#2187AB]">
                  {{ store.total.toFixed(2) }} {{ store.currency }}
                </dd>
              </div>
            </dl>
          </div>
        }
      </div>
    </div>

    <div class="space-y-6">
      <div class="p-6 border rounded-xl shadow-sm space-y-6">
        @if (isLoading()) {
          <p class="rounded-lg bg-sky-50 p-3 text-sm text-sky-800" role="status">
            {{ 'checkout.calculatingDelivery' | translate }}
          </p>
        }
        @if (deliveryErrorKey(); as errorKey) {
          <div class="rounded-lg border border-red-200 bg-red-50 p-3" role="alert">
            <p class="text-sm text-red-800">{{ errorKey | translate }}</p>
            @if (showAddressErrorAction()) {
              <button
                type="button"
                (click)="editAddress()"
                class="mt-2 font-medium text-sm text-red-800 underline focus:outline-none focus:ring-2 focus:ring-red-300"
              >
                {{ 'addressReadiness.pinpointAction' | translate }}
              </button>
            }
          </div>
        }
        <fieldset
          role="radiogroup"
          aria-labelledby="checkout-payment-method-label"
          class="space-y-4"
        >
          <legend id="checkout-payment-method-label" class="text-xl font-medium">
            {{ 'Select payment method' | translate }}
          </legend>
          @for (warning of walletWarnings(); track warning.code) {
            <div role="alert" class="alert alert-soft alert-warning text-sm">
              {{ warning.message }}
            </div>
          }
          @for (method of paymentMethods(); track method.id) {
            <label
              class="relative flex flex-row items-start justify-between gap-3 rounded border p-2.5 transition-colors focus-within:border-blue focus-within:ring-2 focus-within:ring-blue/20"
              [class.hover:border-blue]="method.available && !method.selected"
              [class.border-blue]="method.selected && method.available"
              [class.border-base-200]="!method.selected || !method.available"
              [class.text-gray-400]="!method.available"
              [class.cursor-not-allowed]="!method.available"
              [class.cursor-pointer]="method.available"
            >
              <div class="min-w-0 grow flex flex-col gap-1">
                <div class="flex items-center justify-between">
                  <div class="flex items-center gap-3">
                    <div
                      [class.text-gray-400]="!method.available"
                      [class.text-base-content]="
                        method.available &&
                        method.icon === 'wallet' &&
                        !method.selected
                      "
                      [class.text-blue]="
                        method.available &&
                        (method.icon !== 'wallet' || method.selected)
                      "
                    >
                      @switch (method.icon) {
                        @case ('cash') {
                          <sham-i-cash-on-delivery class="*:size-5" />
                        }
                        @case ('wallet') {
                          <svg
                            class="h-5 w-[22px]"
                            viewBox="0 0 22 20"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            aria-hidden="true"
                          >
                            <path
                              d="M14.3323 1.57672C14.0703 1.50649 13.7283 1.50005 12.75 1.50005H8.75C6.84318 1.50005 5.48851 1.50164 4.46085 1.63981C3.45476 1.77507 2.87511 2.02874 2.4519 2.45195C2.02869 2.87516 1.77502 3.45481 1.63976 4.4609C1.50159 5.48856 1.5 6.84323 1.5 8.75005V10.75C1.5 12.6569 1.50159 14.0115 1.63976 15.0392C1.77502 16.0453 2.02869 16.6249 2.4519 17.0481C2.87511 17.4714 3.45476 17.725 4.46085 17.8603C5.48851 17.9985 6.84318 18 8.75 18H14.75C16.1854 18 17.1865 17.9985 17.9419 17.8969C18.6757 17.7982 19.0642 17.6178 19.341 17.341C19.6178 17.0643 19.7982 16.6757 19.8969 15.9419C19.9984 15.1866 20 14.1855 20 12.75V10.75C20 9.31463 19.9984 8.31352 19.8969 7.55817C19.7982 6.82439 19.6178 6.43582 19.341 6.15906C19.0642 5.8823 18.6757 5.70185 17.9419 5.6032C17.1865 5.50164 16.1854 5.50005 14.75 5.50005H8.75C8.33579 5.50005 8 5.16426 8 4.75005C8 4.33584 8.33579 4.00005 8.75 4.00005L14.8049 4.00005C15.227 4.00004 15.6239 4.00004 15.9964 4.00346C15.9898 3.56621 15.9717 3.34804 15.9233 3.16771C15.7153 2.39125 15.1088 1.78477 14.3323 1.57672ZM17.498 4.05268C17.4919 3.53499 17.4676 3.13527 17.3722 2.77948C17.0255 1.48538 16.0147 0.474579 14.7206 0.127828C14.2419 -0.000429302 13.6838 -0.000245361 12.8658 2.4171e-05C12.8278 3.6688e-05 12.7892 4.93837e-05 12.75 4.93837e-05L8.69358 4.90261e-05C6.85582 3.31713e-05 5.40019 2.05338e-05 4.26098 0.153184C3.08856 0.310812 2.13961 0.642929 1.39124 1.39129C0.64288 2.13966 0.310763 3.0886 0.153135 4.26103C-2.81632e-05 5.40024 -1.55332e-05 6.85587 3.21614e-07 8.69363V10.8065C-1.55332e-05 12.6442 -2.81632e-05 14.0999 0.153135 15.2391C0.310763 16.4115 0.64288 17.3604 1.39124 18.1088C2.13961 18.8572 3.08856 19.1893 4.26098 19.3469C5.40019 19.5001 6.85583 19.5001 8.69359 19.5H14.8049C16.1725 19.5001 17.2748 19.5001 18.1418 19.3835C19.0419 19.2625 19.7997 19.0036 20.4017 18.4017C21.0036 17.7998 21.2625 17.0419 21.3835 16.1418C21.5 15.2748 21.5 14.1725 21.5 12.8049V10.6952C21.5 9.32759 21.5 8.22527 21.3835 7.3583C21.2625 6.45819 21.0036 5.70032 20.4017 5.0984C19.7997 4.49648 19.0419 4.23759 18.1418 4.11657C17.94 4.08945 17.7256 4.06865 17.498 4.05268Z"
                              fill="currentColor"
                              fill-rule="evenodd"
                              clip-rule="evenodd"
                            />
                          </svg>
                        }
                        @case ('paymera') {
                          <sham-i-paymera-logo class="*:size-5" />
                        }
                      }
                    </div>
                    <p class="text-xs font-medium">
                      {{ method.name | translate }}
                      @if (method.icon === 'wallet' && method.balance !== null) {
                        ({{ method.balance }} {{ method.balanceCurrency }})
                      }
                    </p>
                  </div>
                </div>

                <p
                  class="text-[10px] leading-4"
                  [class.text-gray-400]="!method.available"
                  [class.text-gray-500]="method.available"
                >
                  {{ method.description | translate }}
                </p>
              </div>

              <input
                type="radio"
                name="checkout-payment-method"
                [value]="method.id"
                [checked]="method.selected"
                [disabled]="!method.available"
                (change)="selectPaymentMethod(method.id)"
                class="checkbox checkbox-primary checkbox-sm mt-0.5 rounded-sm"
              />
            </label>
          }
        </fieldset>
        <h3 class="text-xl font-medium">{{ 'Order Summary' | translate }}</h3>

        <div class="space-y-4">
          <div class="flex justify-between text-[#4D4D4D] text-sm">
            <span>{{ 'Subtotal' | translate }}:</span>
            <span class="font-medium text-gray-900"
              >{{ subtotal().toFixed(2) }} {{ currency() }}</span
            >
          </div>
          <hr class="border" />
          <div class="flex justify-between text-[#4D4D4D] text-sm">
            <span>{{ 'Shipping' | translate }}:</span>
            <span class="font-medium text-gray-900"
              >{{ shipping().toFixed(2) }} {{ currency() }}</span
            >
          </div>

          @if (totalDiscount()) {
            <div class="flex justify-between text-[#4D4D4D] text-sm">
              <span>{{ 'totalDiscount' | translate }}</span>
              <span class="font-medium text-gray-900"
                >{{ totalDiscount().toFixed(2) }} {{ currency() }}</span
              >
            </div>
          }

          <hr class="border" />

          <div class="flex justify-between items-center">
            <span class="font-bold text-[#4D4D4D]">{{ 'Total' | translate }}:</span>
            <span class="text-lg font-bold text-[#2596BE]">
              {{ total().toFixed(2) }} {{ currency() }}
            </span>
          </div>
        </div>

        <button
          (click)="placeOrder()"
          [disabled]="!canPlaceOrder() || isLoading()"
          class="w-full py-2 rounded-lg font-normal text-base transition shadow-lg text-white disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed disabled:shadow-none bg-[#2596BE] hover:bg-[#1a7a9c] shadow-sky-100"
        >
          {{ 'Place Order' | translate }}
        </button>
      </div>
    </div>
  </div>

  @for (store of groupedItems(); track store.sellerId) {
    <app-shop-voucher-modal
      [isModalOpen]="isVoucherModalOpen()[store.sellerId] || false"
      [sellerStoreId]="store.sellerId"
      [sellerSubtotal]="store.subtotal"
      [currentSelectedCouponId]="null"
      [currentSelectedCouponCode]="selectedCoupons()[store.sellerId] || null"
      [availableCoupons]="getSellerCoupons(store.sellerId)"
      (couponSelected)="onCouponSelected(store.sellerId, $event)"
      (closed)="closeVoucherModal(store.sellerId)"
    />
  }
</section>
@if (paymentPopup(); as popup) {
  <app-popup
    [title]="popup.title"
    [message]="popup.message"
    [tone]="popup.tone"
    [buttonsAlign]="popup.buttonsAlign"
    [primaryButton]="popup.primaryButton"
    [secondaryButton]="popup.secondaryButton"
    [closeOnBackdrop]="popup.closeOnBackdrop"
    [showIconBackground]="false"
    (closed)="closePaymentPopup()"
    (primaryAction)="onPaymentPopupPrimaryAction()"
  >
    @if (popup.mode === 'redirecting') {
      <sham-i-square-arrow-reload-01 popupIcon />
    } @else if (popup.mode === 'reconciliation-required') {
      <sham-i-clock-04 popupIcon />
    } @else if (popup.mode === 'retry-available') {
      <sham-i-alert-diamond popupIcon />
    } @else if (popup.mode === 'order-failed') {
      <sham-i-cancel-circle popupIcon />
    }
  </app-popup>
}
